import * as React from 'react';
import classnames from 'classnames';
import { useTheme, createUseStyles } from 'react-jss';
import { Trans } from 'react-i18next';
import { Theme } from '@app/theme';
import { useFormikContext } from 'formik';
import { useFormikError, useOptionalField } from './FormikFields';
import { RawCheckbox } from './RawCheckbox';
import { ErrorMsg } from './ErrorMsg';

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  explanation?: string;
  containerClass?: string;
  value?: any;
  name?: string;
  error?: string;
  multiline?: boolean;
  checkedValue?: any;
  noMargin?: boolean;
  checked?: boolean;
  indeterminate?: boolean;
  disabled?: boolean;
  variant?: 'default' | 'green' | 'red';
  labelColorChange?: boolean;
}

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    marginBottom: theme.marginBase * 3,
    marginTop: theme.marginBase * 3,
    width: '100%',
    display: 'block',
  },
  noMargin: {
    marginBottom: 0,
    marginTop: 0,
  },
  line: {
    display: 'flex',
    alignItems: 'center',

    '&:hover > .checkbox-icon': {
      color: theme.colors.blueAccessible,
    },

    '& > .checkbox-label': {
      ...theme.fonts.caption,
      flex: 1,
      marginLeft: theme.marginBase,
      color: theme.colors.inkSecondary,
      '& a': {
        color: theme.colors.blueAccessible,
      },
    },
  },
  lineGreen: {
    '&:hover > .checkbox-icon': {
      color: theme.colors.green,
    },
  },
  lineRed: {
    '&:hover > .checkbox-icon': {
      color: theme.colors.red,
    },
  },
  multiline: {
    alignItems: 'flex-start',
  },
  explanation: {
    ...theme.fonts.caption,
    color: theme.colors.inkSecondary,
    marginLeft: theme.iconSize.regular + theme.marginBase,
  },
  lineInvalid: {
    '& > .checkbox-icon, &:hover > .checkbox-icon': {
      color: theme.colors.red,
    },
  },
  lineSelected: {
    '& > .checkbox-label': {
      color: theme.colors.inkPrimary,
    },
  },
  lineLabelColor: {
    '&:hover > .checkbox-label': {
      color: theme.colors.inkPrimary,
    },
  },
  lineLabelColorSelected: {
    '& > .checkbox-label, &:hover > .checkbox-label': {
      color: theme.colors.blueAccessible,
    },
  },
  lineGreenLabelColorSelected: {
    '& > .checkbox-label, &:hover > .checkbox-label': {
      color: theme.colors.greenAccessible,
    },
  },
  lineRedLabelColorSelected: {
    '& > .checkbox-label, &:hover > .checkbox-label': {
      color: theme.colors.redAccessible,
    },
  },
}));

export const Checkbox = ({
  label,
  explanation,
  containerClass,
  className,
  error,
  children,
  value,
  name,
  multiline,
  checkedValue,
  onChange,
  noMargin,
  checked,
  variant = 'default',
  labelColorChange,
  ...rest
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const errorMsg = useFormikError({ name, error });

  const { field } = useOptionalField({ ...rest, name });
  const formik = useFormikContext();

  const _onChange = (e: any) => {
    if (onChange) {
      return onChange(e);
    }
    if (!field || !formik || !field.onChange) {
      return;
    }
    if (!checkedValue || !name) {
      return field.onChange(e);
    }
    formik.setFieldValue(
      // @ts-ignore
      name,
      e.target.checked ? checkedValue : undefined,
    );
  };

  const val = value || field.value;
  const isChecked = checked !== undefined ? checked : !!val;

  return (
    <div
      className={classnames(containerClass, classes.container, {
        [classes.noMargin]: !!noMargin,
      })}
    >
      <label
        className={classnames(classes.line, {
          [classes.lineInvalid]: !!errorMsg,
          [classes.lineSelected]: isChecked,
          [classes.multiline]: !!multiline,
          [classes.lineGreen]: variant === 'green',
          [classes.lineRed]: variant === 'red',
          [classes.lineLabelColor]: labelColorChange,
          [classes.lineLabelColorSelected]: labelColorChange && isChecked,
          [classes.lineGreenLabelColorSelected]:
            labelColorChange && variant === 'green' && isChecked,
          [classes.lineRedLabelColorSelected]:
            labelColorChange && variant === 'red' && isChecked,
        })}
      >
        <RawCheckbox
          noLabel
          type="checkbox"
          className="checkbox-icon"
          name={name}
          variant={variant}
          {...field}
          {...rest}
          onChange={_onChange}
          value={val}
          checked={isChecked}
        />
        {(label || children) && (
          <div className="checkbox-label">
            {label ? <Trans i18nKey={label}>{label}</Trans> : children}
          </div>
        )}
      </label>
      <ErrorMsg error={errorMsg} />

      {explanation && (
        <div className={classes.explanation}>
          <Trans i18nKey={explanation}>{explanation}</Trans>
        </div>
      )}
    </div>
  );
};
